import { storage } from '../config/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { firestoreService } from './firestoreService';

export interface FileUploadProgress {
  fileName: string;
  progress: number;
  status: 'pending' | 'uploading' | 'success' | 'error';
  error?: string;
}

export interface FileWithYear {
  file: File;
  fileName: string;
  year: number;
}

class StorageService {
  async selectFolder(): Promise<FileWithYear[]> {
    try {
      const dirHandle = await (window as any).showDirectoryPicker();
      const files: FileWithYear[] = [];

      for await (const [name, handle] of dirHandle.entries()) {
        if (handle.kind === 'file' && name.endsWith('.zip')) {
          const file = await handle.getFile();
          const year = this.extractYearFromFilename(name);

          files.push({
            file,
            fileName: name,
            year,
          });
        }
      }

      return files;
    } catch (err) {
      if ((err as Error).name === 'AbortError') {
        throw new Error('Folder selection was cancelled');
      }
      throw err;
    }
  }

  private extractYearFromFilename(fileName: string): number {
    const yearMatch = fileName.match(/(\d{4})/);
    if (yearMatch) {
      const year = parseInt(yearMatch[1], 10);
      if (year >= 1900 && year <= 2100) {
        return year;
      }
    }
    return new Date().getFullYear();
  }

  async uploadFilesByYear(
    files: FileWithYear[],
    onProgress?: (updates: FileUploadProgress[]) => void
  ): Promise<void> {
    const progressMap = new Map<string, FileUploadProgress>();

    for (const { file, fileName, year } of files) {
      const progressKey = fileName;
      progressMap.set(progressKey, {
        fileName,
        progress: 0,
        status: 'uploading',
      });

      onProgress?.(Array.from(progressMap.values()));

      try {
        const storagePath = `transition/${year}/${fileName}`;
        const fileRef = ref(storage, storagePath);

        await uploadBytes(fileRef, file);

        const downloadUrl = await getDownloadURL(fileRef);

        await firestoreService.createFileMetadata(
          fileName,
          year,
          file.size,
          storagePath
        );

        progressMap.set(progressKey, {
          fileName,
          progress: 100,
          status: 'success',
        });
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        progressMap.set(progressKey, {
          fileName,
          progress: 0,
          status: 'error',
          error: errorMessage,
        });
        console.error(`Failed to upload ${fileName}:`, err);
      }

      onProgress?.(Array.from(progressMap.values()));
    }
  }

  async createDownloadUrl(storagePath: string): Promise<string> {
    const fileRef = ref(storage, storagePath);
    return getDownloadURL(fileRef);
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

  formatBytes(bytes: number, decimals = 2): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return Math.round((bytes / Math.pow(k, i)) * Math.pow(10, dm)) / Math.pow(10, dm) +
      ' ' +
      sizes[i];
  }

  calculateTotalSize(files: FileWithYear[]): number {
    return files.reduce((total, { file }) => total + file.size, 0);
  }

  calculateTotalSizeByYear(files: FileWithYear[]): Map<number, number> {
    const sizeByYear = new Map<number, number>();

    for (const { file, year } of files) {
      const currentSize = sizeByYear.get(year) || 0;
      sizeByYear.set(year, currentSize + file.size);
    }

    return sizeByYear;
  }
}

export const storageService = new StorageService();
